import barba from '@barba/core';
import barbaPrefetch from '@barba/prefetch';
import gsap from 'gsap';

// =============================================================================
// --- 1. UTILITAIRES ---
// =============================================================================

const scrollLock = {
    enable() {
        document.body.style.overflow = 'hidden';
        document.body.style.height = '100vh';
    },
    disable() {
        document.body.style.overflow = '';
        document.body.style.height = '';
    }
};

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function reloadMyModules() {
    // Relance tes scripts JS perso après la transition
    const modules = [
        'initPdwCounter', 'initPdwScrolled', 'initPdwGSAP',
        'initPdwAos', 'initPdwCarousel'
    ];
    modules.forEach(mod => {
        if (typeof window[mod] === 'function') window[mod]();
    });
    if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
}

function runInlineScripts(container) {
    const scripts = container.querySelectorAll('script');
    scripts.forEach(script => {
        const newScript = document.createElement('script');
        if (script.src) {
            newScript.src = script.src;
            newScript.async = false;
        } else {
            newScript.textContent = script.innerHTML;
        }
        Array.from(script.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
        script.parentNode.replaceChild(newScript, script);
    });
}

// =============================================================================
// --- 2. TRANSITION DE NAVIGATION (ZOOM / DEPTH) ---
// =============================================================================

const zoomTransition = (data) => {
    const currentContainer = data.current.container;
    const nextContainer = data.next.container;
    // On récupère le parent (le wrapper Barba) pour figer sa hauteur
    const wrapper = currentContainer.parentNode;

    if (reduceMotion) {
        window.scrollTo(0, 0);
        return gsap.from(nextContainer, { opacity: 0, duration: 0.5 });
    }

    // On prépare la timeline
    const tl = gsap.timeline({
        onStart: () => {
            scrollLock.enable();

            // --- FIX DU FOOTER & HAUTEUR ---
            wrapper.style.height = wrapper.offsetHeight + 'px';
            wrapper.style.overflow = 'hidden';

            // --- GESTION POSITION ABSOLUE ---
            // 1. On fige l'ancien container
            currentContainer.style.position = 'absolute';
            currentContainer.style.top = -window.scrollY + 'px';
            currentContainer.style.left = '0';
            currentContainer.style.width = '100%';
            currentContainer.style.zIndex = 1;
            currentContainer.style.filter = 'blur(0px)';
            currentContainer.style.transformOrigin = 'center 20vh';

            // 2. On prépare le nouveau
            nextContainer.style.position = 'absolute';
            nextContainer.style.top = '0';
            nextContainer.style.left = '0';
            nextContainer.style.width = '100%';
            nextContainer.style.opacity = '0';
            nextContainer.style.zIndex = 2;
            nextContainer.style.filter = 'blur(10px)';

            gsap.set(nextContainer, { scale: 1.1 });

            // Scroll top immédiat
            window.scrollTo(0, 0);
        },
        onComplete: () => {
            scrollLock.disable();

            // --- NETTOYAGE ---
            gsap.set(currentContainer, { clearProps: 'all' });
            gsap.set(nextContainer, { clearProps: 'all' });
            nextContainer.style.opacity = '1';

            // IMPORTANT : On libère la hauteur du wrapper
            wrapper.style.height = '';
            wrapper.style.overflow = '';
        }
    });

    // --- L'ANIMATION "RECUL" ---
    tl.to(currentContainer, {
        scale: 0.9,
        opacity: 0,
        filter: 'blur(5px)',
        duration: 0.8,
        ease: 'power3.inOut'
    });

    tl.to(nextContainer, {
        scale: 1,
        opacity: 1,
        filter: 'blur(0px)',
        duration: 0.8,
        ease: 'power3.inOut'
    }, "<0.1");

    return tl;
};


// =============================================================================
// --- 3. INITIALISATION BARBA ---
// =============================================================================

document.addEventListener('DOMContentLoaded', () => {

    barba.use(barbaPrefetch);

    barba.init({
        debug: true,
        timeout: 7000,
        prevent: ({ el }) => el.classList.contains('no-barba') || el.closest('#wpadminbar'),
        requestError: (trigger, action, url, response) => {
            if (action === 'click' && response.status === 404) barba.force(url);
            return false;
        },

        // --- HOOKS GLOBAUX ---
        views: [],

        // --- TRANSITIONS ---
        transitions: [
            {
                name: 'page-zoom',
                sync: true,
                leave(data) { return true; },
                enter(data) { return zoomTransition(data); }
            }
        ]
    });

    // --- HOOKS LIFECYCLE ---

    barba.hooks.beforeEnter(({ next }) => {
        const parser = new DOMParser();
        const nextDoc = parser.parseFromString(next.html, 'text/html');

        // Mise à jour Titre & Body Class
        document.title = nextDoc.title;
        const nextBodyClass = nextDoc.body.className;
        document.body.className = nextBodyClass;

        // -----------------------------------------------------------------
        // [FIX] INJECTION DES STYLES MANQUANTS (Gravity Forms, etc.)
        // -----------------------------------------------------------------
        const nextStyles = nextDoc.querySelectorAll('link[rel="stylesheet"]');
        nextStyles.forEach(style => {
            // Vérifie si le CSS n'est pas déjà présent dans le head actuel
            if (!document.querySelector(`link[href="${style.href}"]`)) {
                const newLink = document.createElement('link');
                newLink.rel = 'stylesheet';
                newLink.href = style.href;
                if (style.media) newLink.media = style.media;
                document.head.appendChild(newLink);
            }
        });
        // -----------------------------------------------------------------

        // Scripts Footer & Admin Bar
        const nextFooter = nextDoc.getElementById('wp-footer-scripts');
        const currFooter = document.getElementById('wp-footer-scripts');
        if (nextFooter && currFooter) {
            currFooter.innerHTML = nextFooter.innerHTML;
            runInlineScripts(currFooter);
        }

        const nextAdmin = nextDoc.getElementById('wp-admin-bar-edit');
        const currAdmin = document.getElementById('wp-admin-bar-edit');
        if (nextAdmin && currAdmin) currAdmin.replaceWith(nextAdmin);
    });

    barba.hooks.after((data) => {
        reloadMyModules();

        // Gestion Menu Active
        document.querySelectorAll('.current-menu-item, .current_page_item, .active').forEach(el => {
            el.classList.remove('current-menu-item', 'current_page_item', 'active');
        });
        const currentPath = window.location.href.replace(/\/$/, "");
        const activeLink = document.querySelector(`a[href="${currentPath}"], a[href="${currentPath}/"]`);
        if (activeLink) activeLink.closest('.menu-item')?.classList.add('current-menu-item', 'active');

        if (typeof gtag === 'function') {
            gtag('event', 'page_view', { 'page_title': document.title, 'page_location': location.href });
        }
    });

    barba.hooks.afterEnter(({ next }) => {
        runInlineScripts(next.container);

        if (window.jQuery) {
            // Gravity Forms : Initialisation dynamique
            if (window.gform) {
                // On cherche le formulaire dans le nouveau contenu pour trouver son ID
                const form = next.container.querySelector('.gform_wrapper form');
                if (form && form.id) {
                    const formId = form.id.split('_')[1]; // Ex: gform_4 -> 4
                    jQuery(document).trigger('gform_post_render', [formId, 1]);
                } else {
                    // Fallback si pas trouvé (cas spécifique ou formulaire caché)
                    jQuery(document).trigger('gform_post_render', [1, 1]);
                }
            }

            // Contact Form 7
            if (typeof wpcf7 !== 'undefined') {
                const forms = next.container.querySelectorAll('.wpcf7 > form');
                forms.forEach(form => wpcf7.init(form));
            }
        }
    });
});